import { useEffect, useRef, useCallback, useState } from 'react'
import { WebSocketService } from '../services/WebSocketService'
import { routingActions } from '../contexts/RoutingContext'

type Dispatch = (action: ReturnType<typeof routingActions[keyof typeof routingActions]>) => void

export function useWebSocket(url: string, dispatch: Dispatch) {
  const serviceRef = useRef<WebSocketService | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    const service = new WebSocketService(url)
    serviceRef.current = service

    service.onRoutingUpdate((message) => {
      dispatch(routingActions.setConnections(message.connections))
      dispatch(routingActions.setLastUpdate(new Date()))
    })

    service.onConnectionStatusChange((connected) => {
      setIsConnected(connected)
      dispatch(routingActions.setConnectionStatus(connected ? 'connected' : 'disconnected'))
    })

    service.connect().catch((err) => {
      console.error('WebSocket connection failed:', err)
    })

    return () => {
      service.disconnect()
      serviceRef.current = null
    }
  }, [url, dispatch])

  const sendConnectionRequest = useCallback(
    (sourceId: string, destinationId: string, action: 'connect' | 'disconnect') => {
      if (!serviceRef.current) {
        return Promise.reject(new Error('WebSocket not initialized'))
      }
      return serviceRef.current.sendConnectionRequest(sourceId, destinationId, action)
    },
    []
  )

  return { sendConnectionRequest, isConnected }
}
